import { AssignBar, assignLabel, useAssignActions } from './assign-bar'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useInboxAccounts } from '@/hooks/use-integrations'
import { DisconnectedBanner } from '@/components/shared/reconnect-account'
import { isAccountDown, isSandboxAccount } from '@/lib/account-status'
import { Send, Sparkles, X, Search, MoreVertical, Pin, PinOff, MailOpen, Users, Star, PauseCircle, ChevronDown, Paperclip, Loader2, Image as ImageIcon, FolderOpen, Reply, Plus, IdCard, CreditCard, Link2, BellRing, BarChart3, Images, Lock, LockKeyhole, MessageCircle, ChevronLeft, Info, Hand, UserRoundCog } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/misc'
import { useAutosizeTextarea, mobileMaxHeight } from '@/hooks/use-autosize-textarea'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu'
import { Loading, EmptyState, ErrorState } from '@/components/shared/feedback'
import { api, apiError } from '@/lib/api-client'
import { cn, initials } from '@/lib/utils'
import { i18n } from '@/lib/i18n'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { getSocket } from '@/lib/socket'
import {
  useChatRealtime,
  useConversation,
  useMessages,
  useFilteredMessages,
  useMessageSenders,
  useSendMessage,
  useSetAiMode,
  useSetRequireMention,
  useSetAiPause,
  useTogglePin,
  useSetConversationTab,
  useMarkUnread,
  useReactMessage,
  useUndoMessage,
  useDeleteMessage,
  type ChatMessage,
  type SenderFilter,
  useGroupMembers,
} from '@/hooks/use-conversations'
import { MessageBubble, type MessageActions } from './message-bubble'
import { ForwardDialog } from './forward-dialog'
import { ResourcesDialog } from './resources-dialog'
import { ConversationTags } from './conversation-tags'
import { GroupMembersMenu } from './group-members-menu'
import { chatHeaderLayout } from './chat-header-layout'
import { useElementWidth } from '@/hooks/use-element-width'
import { StickerPicker } from './sticker-picker'
import { MediaLibraryDialog } from './media-library-dialog'
import { QuickMessagesPopover } from './quick-messages-popover'
import { SendExtrasDialog } from './send-extras-dialog'
import { ReminderDialog } from './reminder-dialog'
import { PollDialog } from './poll-dialog'
import {
  detectMentionQuery, filterMembers, insertMention, resolveMentions,
  MENTION_ALL_UID, type PendingMention,
} from './mentions'
import { ViewAsStaffButton } from './view-as-staff'
import { ProductPickerButton } from './product-picker'
import { CreateOrderButton } from './create-order-dialog'
import { platformLabel, messagePreview } from './lib'
import { PLATFORM } from '@/lib/channel-groups'
import { ContactProfileMenu } from './contact-profile-menu'
import { useAiBots, useAiConfig } from '@/hooks/use-ai'
import {
  AI_MODE_LABEL, CONV_AI_CHOICES, convAiChoice, effectiveConvMode, effectiveModeText, type ConvAiChoice,
} from '@/lib/ai-khung'
import { useOrgMembers } from '@/hooks/use-settings'
import { useAuthStore } from '@/stores/auth-store'
import { useComposerTools } from '@/hooks/use-composer-tools'
import { MobileComposerToolbar, type ComposerToolContext } from './composer-tools-sheet'

/**
 * Chế độ AI của hội thoại (khung ba tầng): Theo nhân viên AI (mặc định — giờ làm & chế độ của nhân viên AI phụ trách kênh)
 * · Thủ công · Gợi ý · Tự động (lựa chọn tay, chỉ cho hội thoại này). Xem lib/ai-khung.ts.
 */

/** Gợi ý AI: nhãn tiếng Việt + màu cho từng giọng văn */
type AiTone = 'concise' | 'friendly' | 'detailed'
interface AiSuggestion {
  suggestionId: string
  tone: AiTone
  text: string
}
const TONE_META: Record<AiTone, { labelKey: string; cls: string }> = {
  concise: { labelKey: 'chatPanel.tone.concise', cls: 'bg-blue-500/15 text-blue-600 dark:text-blue-400' },
  friendly: { labelKey: 'chatPanel.tone.friendly', cls: 'bg-pink-500/15 text-pink-600 dark:text-pink-400' },
  detailed: { labelKey: 'chatPanel.tone.detailed', cls: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
}

/**
 * Bộ lọc tin nhắn theo người gửi — lọc TẠI MÁY CHỦ trên toàn hội thoại (backend chat/message-origin.ts):
 * AI = nhân viên AI / agent ngoài tự gửi; Nhân viên = nhân viên gửi trong eCDP (kể cả duyệt gợi ý AI);
 * Tự động & khác = tự động hoá, gửi ngoài eCDP (điện thoại / app), ZNS, hệ thống, tin cũ không rõ nguồn.
 */
const SENDER_FILTERS: { value: SenderFilter; labelKey: string; emptyKey: string }[] = [
  { value: 'all', labelKey: 'chatPanel.filter.all', emptyKey: '' },
  { value: 'contact', labelKey: 'chatPanel.filter.contact', emptyKey: 'chatPanel.filter.contactEmpty' },
  { value: 'ai', labelKey: 'chatPanel.filter.ai', emptyKey: 'chatPanel.filter.aiEmpty' },
  { value: 'staff', labelKey: 'chatPanel.filter.staff', emptyKey: 'chatPanel.filter.staffEmpty' },
  { value: 'other', labelKey: 'chatPanel.filter.other', emptyKey: 'chatPanel.filter.otherEmpty' },
]
const ALL_STAFF = '__all__'

/** Các mốc tạm dừng AI (phút) */
const PAUSE_OPTIONS = [
  { minutes: 30, labelKey: 'chatPanel.pause.m30' },
  { minutes: 60, labelKey: 'chatPanel.pause.h1' },
  { minutes: 240, labelKey: 'chatPanel.pause.h4' },
  { minutes: 1440, labelKey: 'chatPanel.pause.h24' },
]

/** Khung chat trung tâm cho hội thoại đang chọn. */
/**
 * `onBack` có mặt = đang ở bản điện thoại (màn chat toàn màn hình): thêm nút quay lại danh sách ở đầu khung,
 * gọn các nút ở đầu khung; `onOpenInfo` mở hồ sơ khách (hộp trượt) vì cột phải không còn chỗ.
 */
export function ChatPanel({ convId, onBack, onOpenInfo }: { convId: string; onBack?: () => void; onOpenInfo?: () => void }) {
  const { t } = useTranslation('convChat')
  const mobile = !!onBack
  // Đầu khung gọn lại theo bề rộng THẬT của khung chat (cột giữa, chỉ còn ~450–650px trên máy tính nhỏ / máy tính bảng) — xem chat-header-layout.ts.
  const [rootRef, panelWidth] = useElementWidth<HTMLDivElement>()
  const { compact, tight } = chatHeaderLayout(panelWidth, mobile)
  const queryClient = useQueryClient()
  const { data: conv } = useConversation(convId)
  const { data: msgData, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } = useMessages(convId)
  const sendMessage = useSendMessage(convId)
  const setAiMode = useSetAiMode(convId)
  // Chế độ hiệu lực + nguồn ("theo Lễ tân AI · ngoài giờ") — tính từ nhân viên AI phụ trách kênh.
  const { data: aiBotsData } = useAiBots()
  const { data: aiConfig } = useAiConfig()
  const setRequireMention = useSetRequireMention(convId)
  const setAiPause = useSetAiPause(convId)
  const togglePin = useTogglePin()
  const setConvTab = useSetConversationTab()
  const markUnread = useMarkUnread()
  const reactMessage = useReactMessage(convId)
  const undoMessage = useUndoMessage(convId)
  const deleteMessage = useDeleteMessage(convId)
  const { typingName, aiTyping, aiDraft, clearAiDraft } = useChatRealtime(convId)

  const [text, setText] = useState('')
  // Ảnh DÁN vào ô soạn tin — chờ trong hàng đợi, bấm Gửi mới thật sự gửi
  // (dán nhầm thì bấm × xoá). url = objectURL để preview.
  const [pendingImages, setPendingImages] = useState<{ file: File; url: string }[]>([])
  // ── Chế độ gửi: external (khách) / internal (nội bộ) / private (riêng tư).
  // Backend enforce: internal/private KHÔNG BAO GIỜ đi ra kênh khách.
  const [sendMode, setSendMode] = useState<'external' | 'internal' | 'private'>('external')
  const [internalMentions, setInternalMentions] = useState<{ id: string; fullName: string }[]>([])
  const myId = useAuthStore((s) => s.user?.id)
  // Tiện ích ghim ở thanh công cụ điện thoại — lưu THEO TÀI KHOẢN (đồng bộ mọi thiết bị); xem dưới quyền người khác thì chỉ đọc.
  const impersonating = useAuthStore((s) => !!s.user?.impersonatedBy)
  const composerTools = useComposerTools(myId, { readOnly: impersonating })
  // Danh bạ nội bộ cho @mention — endpoint mở cho mọi nhân viên (member thường
  // gọi /settings/team sẽ bị 403 nên không dùng useTeam ở đây).
  const { data: orgMembers = [] } = useOrgMembers(sendMode !== 'external')
  // Danh sách tag đã chèn, THEO THỨ TỰ. Vị trí ký tự được quét lại lúc gửi
  // (xem mentions.ts) vì người dùng có thể sửa chữ phía trước bất cứ lúc nào.
  const [pendingMentions, setPendingMentions] = useState<PendingMention[]>([])
  const [mentionQuery, setMentionQuery] = useState<{ triggerAt: number; query: string } | null>(null)
  const [mentionIndex, setMentionIndex] = useState(0)
  const composerRef = useRef<HTMLTextAreaElement>(null)
  // Ô soạn giãn theo nội dung (điện thoại: tối đa 40% vùng nhìn thấy, bàn phím mở thì co theo; máy tính: 160px) rồi cuộn bên trong.
  const composerMax = useCallback(() => (mobile ? mobileMaxHeight(window.visualViewport?.height ?? window.innerHeight) : 160), [mobile])
  useAutosizeTextarea(composerRef, text, { min: mobile ? 44 : 60, max: composerMax })
  // Nhận / giao hội thoại: máy tính là thanh ở đầu khung (AssignBar); điện thoại gom vào menu ⋮ nên cần logic dùng chung ở đây.
  const assign = useAssignActions(convId, conv)
  // Trạng thái SỐNG của tài khoản gửi (pool Zalo), dùng chung cache với bộ chọn
  // tài khoản; tự làm mới khi socket báo `zalo:account-status`.
  const { data: channelAccounts } = useInboxAccounts()
  // Hội thoại chat thử AI ("Thử phiên bản — …", "Kiểm tra — …") nằm trên kênh ảo
  // "🧪 AI Sandbox" — không bao giờ kết nối, nên không có gì để cảnh báo.
  const sendingAccount = isSandboxAccount(conv?.channelAccount)
    ? undefined
    : channelAccounts?.find((a) => a.id === conv?.channelAccount?.id)

  const isGroup = conv?.threadType === 'group'
  // Menu "Xem trang cá nhân/Nhắn tin riêng/Thêm bạn" (giống Zalo gốc) chỉ dùng được với Zalo cá nhân —
  // API kết bạn/trạng thái bạn bè không có ở OA/Bot/Facebook/kênh khác.
  const isZaloUser = conv?.channelAccount?.platform === PLATFORM.ZALO_USER
  const { data: groupMembers = [] } = useGroupMembers(convId, isGroup)
  // uid → avatar thành viên nhóm, để vẽ ảnh nhỏ cạnh tên người gửi trong từng dòng tin (giống Zalo gốc).
  const groupMemberAvatarByUid = useMemo(() => {
    const map: Record<string, string | null> = {}
    for (const m of groupMembers) map[m.uid] = m.avatarUrl ?? null
    return map
  }, [groupMembers])
  // Gõ '@' gợi ý theo chế độ gửi:
  //   external (nhóm Zalo) → thành viên nhóm + '@Tất cả' (uid đặc biệt của Zalo)
  //   internal/private     → NHÂN VIÊN nội bộ (tag đồng nghiệp, khách không thấy)
  const mentionCandidates = mentionQuery
    ? filterMembers(
        sendMode !== 'external'
          ? orgMembers
              .filter((m) => m.id !== myId && !internalMentions.some((im) => im.id === m.id))
              .map((m) => ({ uid: m.id, name: m.fullName || t('chatPanel.staff'), avatarUrl: m.avatarUrl }))
          : [{ uid: MENTION_ALL_UID, name: 'Tất cả', avatarUrl: null }, ...groupMembers], // i18n-ignore: tên chèn vào nội dung tin gửi khách, không đổi theo ngôn ngữ giao diện
        mentionQuery.query,
      ).slice(0, 8)
    : []
  const [searchOpen, setSearchOpen] = useState(false)
  const [resourcesOpen, setResourcesOpen] = useState(false)
  const [msgSearch, setMsgSearch] = useState('')
  const [senderFilter, setSenderFilter] = useState<SenderFilter>('all')
  const [staffId, setStaffId] = useState('')
  const filtered = useFilteredMessages(convId, senderFilter, staffId)
  const senders = useMessageSenders(convId, senderFilter === 'staff')
  const [uploading, setUploading] = useState(false)
  const [suggestions, setSuggestions] = useState<AiSuggestion[] | null>(null)
  const [suggesting, setSuggesting] = useState(false)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null)
  const [forwardMsg, setForwardMsg] = useState<ChatMessage | null>(null)
  const [extrasTab, setExtrasTab] = useState<'card' | 'bank' | 'link' | null>(null)
  const [reminderOpen, setReminderOpen] = useState(false)
  const [pollOpen, setPollOpen] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const typingSentRef = useRef(false)

  const allMessages = useMemo(() => msgData?.messages ?? [], [msgData])

  // Lọc theo người gửi: máy chủ trả tin đã lọc (toàn hội thoại); từ khoá lọc tiếp phía client.
  const messages = useMemo(() => {
    const source = senderFilter === 'all' ? allMessages : (filtered.data?.messages ?? [])
    const q = msgSearch.trim().toLowerCase()
    return q ? source.filter((m) => (m.content ?? '').toLowerCase().includes(q)) : source
  }, [allMessages, filtered.data, msgSearch, senderFilter])
  const listLoading = senderFilter === 'all' ? isLoading : filtered.isLoading
  const listError = senderFilter === 'all' ? isError : filtered.isError
  const activeFilter = SENDER_FILTERS.find((f) => f.value === senderFilter)!

  // ── "Đã xem": từ conversation.seenBy (uid → mốc tin mới nhất đã xem) tính
  // ra tin CỦA MÌNH cuối cùng mà mỗi người đã xem tới → đặt avatar dưới tin đó
  // (giống Zalo). Nhóm: tra tên/avatar từ thành viên nhóm; 1-1: chính là khách.
  const seenMarkers = useMemo(() => {
    const map: Record<string, { name: string; avatarUrl?: string | null }[]> = {}
    const seenBy = conv?.seenBy
    if (!seenBy) return map
    const accountUid = conv?.channelAccount?.externalUid
    for (const [uid, mark] of Object.entries(seenBy)) {
      if (!mark?.msgSentAt || (accountUid && uid === accountUid)) continue
      const seenT = new Date(mark.msgSentAt).getTime()
      let target: ChatMessage | null = null
      for (const m of allMessages) {
        if (m.senderType !== 'self' || m.isDeleted) continue
        // Tin nội bộ/riêng tư khách không thấy → không gắn "Đã xem" vào đó.
        if (m.visibility && m.visibility !== 'external') continue
        if (new Date(m.sentAt).getTime() <= seenT) target = m
        else break
      }
      if (!target) continue
      const member = isGroup ? groupMembers.find((g) => g.uid === uid) : null
      const viewer = isGroup
        ? { name: member?.name || t('chatPanel.userFallback', { id: uid.slice(-4) }), avatarUrl: member?.avatarUrl ?? null }
        : { name: conv?.contact?.fullName || t('chatPanel.customer'), avatarUrl: conv?.contact?.avatarUrl ?? null }
      ;(map[target.id] ??= []).push(viewer)
    }
    return map
  }, [conv?.seenBy, conv?.channelAccount?.externalUid, conv?.contact, allMessages, groupMembers, isGroup, t])

  // Còn hiệu lực tạm dừng AI?
  const pausedUntil = conv?.aiPausedUntil ? new Date(conv.aiPausedUntil) : null
  const isPaused = !!pausedUntil && pausedUntil.getTime() > Date.now()

  // Chế độ AI: lựa chọn của hội thoại (Theo nhân viên AI / tay) + chế độ đang áp lúc này và vì sao.
  const aiChoice = conv ? convAiChoice(conv) : 'follow'
  const aiEffective = conv
    ? effectiveConvMode({
        conv: { aiMode: conv.aiMode, aiModeReason: conv.aiModeReason, aiPausedUntil: conv.aiPausedUntil, channelAccountId: conv.channelAccount?.id },
        bots: aiBotsData?.bots,
        aiOn: aiConfig ? aiConfig.autoReplyEnabled && aiConfig.enabled !== false && !aiConfig.platformAiDisabled : undefined,
      })
    : null
  const aiModeText = aiEffective ? effectiveModeText(aiEffective) : ''

  // Tự cuộn xuống cuối khi có tin mới / mở hội thoại / có chỉ báo đang nhập. Dựa vào id tin CUỐI (không phải số tin):
  // tải thêm tin cũ ở đầu danh sách làm số tin tăng nhưng không được kéo người đọc xuống cuối.
  const lastMessageId = messages[messages.length - 1]?.id
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [lastMessageId, senderFilter, msgSearch, typingName, aiTyping, convId])

  // Kéo lên gần đầu khung chat → tải trang tin cũ hơn (chỉ ở chế độ xem đầy đủ; bộ lọc người gửi/tìm kiếm tự có
  // giới hạn riêng). Giữ nguyên vị trí đang đọc sau khi tin cũ chèn vào phía trên.
  const keepScrollRef = useRef<{ height: number; top: number } | null>(null)
  const canLoadOlder = senderFilter === 'all' && !msgSearch.trim()
  const loadOlder = useCallback(() => {
    const el = scrollRef.current
    if (!el || !canLoadOlder || !hasNextPage || isFetchingNextPage) return
    keepScrollRef.current = { height: el.scrollHeight, top: el.scrollTop }
    void fetchNextPage()
  }, [canLoadOlder, hasNextPage, isFetchingNextPage, fetchNextPage])
  useLayoutEffect(() => {
    const el = scrollRef.current
    const keep = keepScrollRef.current
    if (!el || !keep || isFetchingNextPage) return
    el.scrollTop = el.scrollHeight - keep.height + keep.top
    keepScrollRef.current = null
  }, [isFetchingNextPage])

  // Chỉ báo "đang nhập" gửi tới server (throttle đơn giản).
  function emitTyping() {
    if (typingSentRef.current) return
    typingSentRef.current = true
    getSocket().emit('typing:start', convId)
    setTimeout(() => {
      typingSentRef.current = false
    }, 2500)
  }

  /**
   * Đổi chế độ gửi. internal/private → external: XOÁ nội dung đang soạn (không
   * mang tin nội bộ sang chế độ gửi khách) + cảnh báo rõ ràng chống gửi nhầm.
   */
  function switchSendMode(next: 'external' | 'internal' | 'private') {
    if (next === sendMode) return
    if (next !== 'external' && pendingImages.length > 0) {
      // Ảnh chờ chỉ gửi được ra kênh khách — rời chế độ khách thì bỏ hàng đợi.
      pendingImages.forEach((p) => URL.revokeObjectURL(p.url))
      setPendingImages([])
      toast.info(t('chatPanel.imagesDiscarded'))
    }
    if (sendMode !== 'external' && next === 'external') {
      setText('')
      setInternalMentions([])
      if (replyTo?.visibility && replyTo.visibility !== 'external') setReplyTo(null)
      toast.warning(t('chatPanel.nextMessageToCustomer'))
    }
    if (next !== 'external') {
      toast.info(next === 'internal'
        ? t('chatPanel.internalModeToast')
        : t('chatPanel.privateModeToast'))
    }
    setSendMode(next)
  }

  // Reply một tin nội bộ → composer buộc về đúng chế độ đó (reply nội bộ phải
  // giữ nội bộ; backend cũng chặn chiều ngược lại).
  useEffect(() => {
    const v = replyTo?.visibility
    if ((v === 'internal' || v === 'private') && sendMode === 'external') setSendMode(v)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyTo])

  async function handleSend() {
    const content = text.trim()
    const hasImages = sendMode === 'external' && pendingImages.length > 0
    if ((!content && !hasImages) || sendMessage.isPending || uploading) return

    // ── Có ảnh dán chờ gửi: upload lần lượt, chữ đang soạn thành caption ảnh 1 ──
    if (hasImages) {
      const imgs = pendingImages
      setText('')
      setPendingImages([])
      setUploading(true)
      getSocket().emit('typing:stop', convId)
      try {
        // MỘT request chứa toàn bộ ảnh → backend gửi thành MỘT tin Zalo (album),
        // chỉ tính một lượt rate-limit. Gửi từng ảnh một từng dính lỗi
        // "Gửi quá nhanh" (burst 5 tin/30s) khi dán nhiều ảnh.
        const form = new FormData()
        imgs.forEach((p) => form.append('file', p.file))
        if (content) form.append('caption', content)
        await api.post(`/conversations/${convId}/messages/image`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        imgs.forEach((p) => URL.revokeObjectURL(p.url))
        queryClient.invalidateQueries({ queryKey: ['conversation-messages', convId] })
        queryClient.invalidateQueries({ queryKey: ['conversations'] })
      } catch (err) {
        // Khôi phục để gửi lại — không mất ảnh lẫn nội dung
        setText(content)
        setPendingImages(imgs)
        toast.error(apiError(err))
      } finally {
        setUploading(false)
      }
      return
    }

    // ── Nhánh NỘI BỘ / RIÊNG TƯ: backend save-only, không đi ra kênh khách ──
    if (sendMode !== 'external') {
      setText('')
      getSocket().emit('typing:stop', convId)
      try {
        await sendMessage.mutateAsync({
          content,
          visibility: sendMode,
          mentionUserIds: internalMentions.map((m) => m.id),
          ...(replyTo ? { replyMessageId: replyTo.id } : {}),
        })
        setReplyTo(null)
        setInternalMentions([])
        queryClient.invalidateQueries({ queryKey: ['conversation-messages', convId] })
      } catch (err) {
        setText(content)
        toast.error(apiError(err))
      }
      return
    }

    // Quét vị trí ngay trước khi gửi — chuỗi đã là bản cuối cùng.
    const mentions = resolveMentions(content, pendingMentions)
    setText('')
    setPendingMentions([])
    setMentionQuery(null)
    getSocket().emit('typing:stop', convId)
    try {
      await sendMessage.mutateAsync({
        content,
        ...(replyTo ? { replyMessageId: replyTo.id } : {}),
        ...(mentions.length ? { mentions } : {}),
      })
      setReplyTo(null)
      clearAiDraft()
    } catch (err) {
      setText(content) // khôi phục nội dung để gửi lại
      setPendingMentions(pendingMentions)
      toast.error(apiError(err))
    }
  }

  /** Chọn một người trong danh sách gợi ý '@' → chèn vào ô soạn tin. */
  function pickMention(member: { uid: string; name: string }) {
    const el = composerRef.current
    if (!el || !mentionQuery) return
    const { text: next, caret, token } = insertMention(
      text, mentionQuery.triggerAt, el.selectionStart ?? text.length, member.name,
    )
    setText(next)
    if (sendMode !== 'external') {
      // Tag nội bộ: backend nhận danh sách userId, không cần vị trí ký tự.
      setInternalMentions((prev) =>
        prev.some((m) => m.id === member.uid) ? prev : [...prev, { id: member.uid, fullName: member.name }],
      )
    } else {
      setPendingMentions((prev) => [...prev, { uid: member.uid, token }])
    }
    setMentionQuery(null)
    setMentionIndex(0)
    // Đặt lại con trỏ sau khi React vẽ xong, nếu không caret nhảy về cuối.
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(caret, caret)
    })
  }

  /** Gửi ảnh hoặc tệp từ máy — backend có 2 endpoint riêng */
  async function uploadFiles(files: File[]) {
    if (!files.length) return
    setUploading(true)
    try {
      // Ảnh gộp MỘT request (backend gửi thành một tin Zalo, một lượt rate-limit
      // — chọn nhiều ảnh từng bị "Gửi quá nhanh"); tệp thường vẫn gửi từng cái.
      const images = files.filter((f) => f.type.startsWith('image/'))
      const others = files.filter((f) => !f.type.startsWith('image/'))
      if (images.length > 0) {
        const form = new FormData()
        images.forEach((f) => form.append('file', f))
        await api.post(`/conversations/${convId}/messages/image`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      }
      for (const file of others) {
        const form = new FormData()
        form.append('file', file)
        await api.post(`/conversations/${convId}/messages/file`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      }
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', convId] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setUploading(false)
    }
  }

  async function handleFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    // Phải COPY ra mảng trước: gán value = '' sẽ xoá luôn e.target.files
    // (FileList là tham chiếu sống) → mất tệp vừa chọn, không gửi được gì.
    const files = Array.from(e.target.files ?? [])
    e.target.value = '' // cho phép chọn lại cùng một tệp
    await uploadFiles(files)
  }

  /** Xin AI gợi ý câu trả lời dựa trên nội dung khách vừa hỏi */
  async function handleSuggest() {
    if (suggesting) return
    setSuggesting(true)
    try {
      const { data } = await api.post<{ suggestions: AiSuggestion[] }>('/ai/suggest', {
        conversationId: convId,
      })
      const list = data?.suggestions ?? []
      if (!list.length) {
        toast.error(t('chatPanel.noSuggestion'))
        return
      }
      setSuggestions(list)
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setSuggesting(false)
    }
  }

  /** Thao tác trên từng tin nhắn */
  const messageActions: MessageActions = {
    onReply: (m) => setReplyTo(m),
    onForward: (m) => setForwardMsg(m),
    onReact: async (m, icon) => {
      try {
        await reactMessage.mutateAsync({ messageId: m.id, icon })
      } catch (err) {
        toast.error(apiError(err))
      }
    },
    onUndo: async (m) => {
      try {
        await undoMessage.mutateAsync(m.id)
        toast.success(t('chatPanel.recalledToast'))
      } catch (err) {
        toast.error(apiError(err))
      }
    },
    onDelete: async (m) => {
      try {
        await deleteMessage.mutateAsync({ messageId: m.id, onlyMe: true })
        toast.success(t('chatPanel.deletedForMeToast'))
      } catch (err) {
        toast.error(apiError(err))
      }
    },
  }

  function applyDraft() {
    if (aiDraft) setText(aiDraft.content)
  }

  async function handleModeChange(choice: ConvAiChoice) {
    try {
      const res = await setAiMode.mutateAsync(choice)
      const label = CONV_AI_CHOICES.find((m) => m.value === choice)?.label
      // Đã lưu chế độ nhưng AI chưa trả lời được (công tắc tổng tắt, chưa có nguồn AI…) → nói rõ thay vì báo thành công.
      if (res.warning) toast.warning(res.warning, { description: t('chatPanel.modeSaved', { label }), duration: 10_000 })
      else toast.success(t('chatPanel.modeChanged', { label }))
    } catch (err) {
      toast.error(apiError(err))
    }
  }


  /** 'chung' = bỏ ghi đè, trả về theo cài đặt "trả lời trong nhóm" của nhân viên AI phụ trách. */
  async function handleRequireMention(v: string) {
    const giaTri = v === 'chung' ? null : v === 'bat'
    try {
      await setRequireMention.mutateAsync(giaTri)
      toast.success(
        giaTri === null ? t('chatPanel.mentionDefaultToast')
          : giaTri ? t('chatPanel.mentionOnToast')
            : t('chatPanel.mentionOffToast'),
      )
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function handlePause(minutes: number) {
    try {
      await setAiPause.mutateAsync(minutes)
      toast.success(
        minutes > 0
          ? t('chatPanel.pausedToast', {
            duration: (() => {
              const opt = PAUSE_OPTIONS.find((p) => p.minutes === minutes)
              return opt ? t(opt.labelKey) : t('chatPanel.pause.minutes', { count: minutes })
            })(),
          })
          : t('chatPanel.unpausedToast'),
      )
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function handleTogglePin() {
    try {
      const res = await togglePin.mutateAsync({ id: convId, pin: !conv?.isPinned })
      toast.success(res.isPinned ? t('chatPanel.pinnedToast') : t('chatPanel.unpinnedToast'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  /** Chuyển tab Ưu tiên ↔ Nội bộ. Nội bộ = không phải khách, không tính báo cáo. */
  async function handleMoveTab() {
    const next = conv?.tab === 'internal' ? 'main' : 'internal'
    try {
      await setConvTab.mutateAsync({ id: convId, tab: next })
      toast.success(next === 'internal' ? t('chatPanel.movedInternalToast') : t('chatPanel.movedMainToast'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function handleMarkUnread() {
    try {
      await markUnread.mutateAsync(convId)
      toast.success(t('chatPanel.markedUnreadToast'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  const name = conv?.displayName || conv?.contact?.fullName || t('chatPanel.conversation')

  const sendDisabled = (!text.trim() && !(sendMode === 'external' && pendingImages.length > 0)) || sendMessage.isPending || uploading
  const sendLabel = sendMode === 'external' ? t('chatPanel.sendCustomer') : sendMode === 'internal' ? t('chatPanel.sendInternal') : t('chatPanel.sendPrivate')
  const sendButton = (
    <Button
      className={cn(
        mobile ? 'h-9 w-9 shrink-0 rounded-full p-0' : 'ml-auto h-8 shrink-0 gap-1.5',
        sendMode === 'internal' && 'bg-amber-500 text-white hover:bg-amber-600',
        sendMode === 'private' && 'bg-violet-600 text-white hover:bg-violet-700',
      )}
      onClick={handleSend}
      disabled={sendDisabled}
      aria-label={sendLabel}
    >
      {sendMode === 'internal' ? <Lock className="h-4 w-4" /> : sendMode === 'private' ? <LockKeyhole className="h-4 w-4" /> : <Send className="h-4 w-4" />}
      {!mobile && sendLabel}
    </Button>
  )
  const toolCtx: ComposerToolContext = {
    convId,
    accountId: conv?.channelAccount?.id,
    contact: conv?.contact,
    contactId: conv?.contact?.id,
    canSendProduct: sendMode === 'external',
    uploading,
    onQuickPick: (picked) => setText((prev) => (prev ? `${prev}\n${picked}` : picked)),
    actions: {
      image: () => imageInputRef.current?.click(),
      file: () => fileInputRef.current?.click(),
      library: () => setLibraryOpen(true),
      card: () => setExtrasTab('card'),
      bank: () => setExtrasTab('bank'),
      link: () => setExtrasTab('link'),
      reminder: () => setReminderOpen(true),
      poll: () => setPollOpen(true),
    },
  }

  return (
    <div ref={rootRef} className="flex h-full min-w-0 flex-col">
      {/* Header — "sticky" + nền mờ (giữ cả khi không cuộn trong vùng scroll riêng): bàn phím ảo mở trên di động
          đẩy/dịch cả khung (use-keyboard-viewport.ts) theo từng sự kiện visualViewport, không nền mờ thì mắt thấy
          header như trôi lẫn vào tin nhắn phía sau trong lúc khung đang dịch chuyển. */}
      <div className={cn('sticky top-0 z-20 flex items-center justify-between gap-3 border-b bg-background/95 p-3 backdrop-blur', compact && 'gap-2', mobile && 'px-2 pt-[calc(0.75rem+env(safe-area-inset-top))]')}>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {onBack && (
            <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" aria-label={t('chatPanel.backToList')} onClick={onBack}>
              <ChevronLeft className="h-6 w-6" />
            </Button>
          )}
          {/* Điện thoại: như Lark — đầu khung chat không có ảnh, dành chỗ cho tên (nhóm / tên dài bị cắt rất ngắn nếu có). */}
          {!tight && (() => {
            const headerAvatar = (
              <Avatar className="h-9 w-9 shrink-0">
                {conv?.contact?.avatarUrl && <AvatarImage src={conv.contact.avatarUrl} alt={name} />}
                <AvatarFallback>{initials(name)}</AvatarFallback>
              </Avatar>
            )
            // Hội thoại 1-1 Zalo cá nhân: bấm ảnh mở menu "Xem trang cá nhân/Thêm bạn" giống Zalo gốc
            // (không có "Nhắn tin riêng" — đang đứng ngay trong hội thoại đó rồi).
            if (!isGroup && isZaloUser && conv?.contact?.zaloUid) {
              return (
                <ContactProfileMenu
                  accountId={conv.channelAccount!.id}
                  uid={conv.contact.zaloUid}
                  name={name}
                  avatarUrl={conv.contact.avatarUrl}
                  showMessageAction={false}
                >
                  <button type="button" className="shrink-0 rounded-full">{headerAvatar}</button>
                </ContactProfileMenu>
              )
            }
            return headerAvatar
          })()}
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <p className="min-w-0 truncate text-sm font-semibold" title={name}>{name}</p>
              {/* Nhãn khách xem trong hồ sơ (điện thoại): đầu khung chỉ còn chỗ cho tên. Không co lại để khỏi đè lên tên. */}
              {!mobile && (
                <span className="shrink-0">
                  <ConversationTags
                    contactId={conv?.contact?.id}
                    tags={conv?.contact?.tags ?? []}
                  />
                </span>
              )}
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <p className="min-w-0 truncate text-xs text-muted-foreground">
                {platformLabel(conv?.channelAccount?.platform)}
                {conv?.channelAccount?.displayName ? ` · ${conv.channelAccount.displayName}` : ''}
              </p>
              {isGroup && convId && (
                <GroupMembersMenu convId={convId} members={groupMembers} accountId={isZaloUser ? conv?.channelAccount?.id : undefined} />
              )}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {/* Tìm trong hội thoại */}
          {searchOpen ? (
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                value={msgSearch}
                onChange={(e) => setMsgSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setMsgSearch('')
                    setSearchOpen(false)
                  }
                }}
                placeholder={t('chatPanel.searchPlaceholder')}
                className={cn('h-8 pl-8 pr-7 text-xs', compact ? 'w-32' : 'w-40')}
              />
              <button
                type="button"
                aria-label={t('chatPanel.closeSearch')}
                onClick={() => {
                  setMsgSearch('')
                  setSearchOpen(false)
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={t('chatPanel.search')}
              onClick={() => setSearchOpen(true)}
            >
              <Search className="h-4 w-4" />
            </Button>
          )}

          {/* Thư viện hội thoại — ảnh/video, tệp, liên kết đã trao đổi (khung gọn / điện thoại: nằm trong menu ⋮) */}
          {!compact && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={t('chatPanel.library')}
              title={t('chatPanel.libraryTitle')}
              disabled={!convId}
              onClick={() => setResourcesOpen(true)}
            >
              <Images className="h-4 w-4" />
            </Button>
          )}

          {/* Người phụ trách: Nhận / Giao lại (nút nào hiện do máy chủ quyết định). Khung gọn / điện thoại: hàng riêng dưới đầu khung. */}
          {!compact && <AssignBar convId={convId} conv={conv} />}

          {/* Chế độ AI — gồm cả tạm dừng AI */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn('h-8 gap-1.5 px-2.5 text-xs', compact && 'w-8 px-0')}
                title={aiModeText ? t('chatPanel.aiModeTitle', { mode: aiModeText }) : undefined}
                disabled={!conv || setAiMode.isPending || setAiPause.isPending}
                aria-label={t('chatPanel.aiMode')}
              >
                {isPaused ? (
                  <PauseCircle className="h-3.5 w-3.5 text-warning" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                )}
                <span className={cn('font-medium', compact && 'hidden')}>
                  {aiEffective ? AI_MODE_LABEL[aiEffective.mode] : t('chatPanel.aiMode')}
                  {aiChoice !== 'follow' && <span className="ml-1 font-normal text-muted-foreground">{t('chatPanel.manualChoice')}</span>}
                </span>
                {isPaused && !compact && (
                  <Badge variant="warning" className="px-1 py-0 text-[9px]">
                    {t('chatPanel.paused')}
                  </Badge>
                )}
                {!compact && <ChevronDown className="h-3.5 w-3.5 opacity-60" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                {t('chatPanel.aiMode')}
                {aiModeText && <span className="mt-0.5 block font-medium text-foreground">{t('chatPanel.applying', { mode: aiModeText })}</span>}
              </DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={aiChoice}
                onValueChange={(v) => handleModeChange(v as ConvAiChoice)}
              >
                {CONV_AI_CHOICES.map((m) => (
                  <DropdownMenuRadioItem key={m.value} value={m.value} className="text-sm">
                    <span className="flex flex-col">
                      <span>{m.label}</span>
                      <span className="text-[11px] text-muted-foreground">{m.hint}</span>
                    </span>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>

              {/* Chỉ nhóm mới có ý nghĩa: hội thoại một-một thì mọi câu đều là
                  nói với mình, bắt khách gõ tên bot mới trả lời là vô lý. */}
              {isGroup && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                    {t('chatPanel.mentionOnly')}
                  </DropdownMenuLabel>
                  <DropdownMenuRadioGroup
                    value={conv?.requireMention == null ? 'chung' : conv.requireMention ? 'bat' : 'tat'}
                    onValueChange={(v) => handleRequireMention(v)}
                  >
                    <DropdownMenuRadioItem value="chung" className="text-sm">
                      {t('chatPanel.followAiStaff')}
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="bat" className="text-sm">
                      {t('chatPanel.enableForGroup')}
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="tat" className="text-sm">
                      {t('chatPanel.disableForGroup')}
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                {isPaused ? t('chatPanel.pausedUntil', { time: pausedUntil?.toLocaleTimeString(i18n.language || 'vi', { hour: '2-digit', minute: '2-digit' }) }) : t('chatPanel.pauseAi')}
              </DropdownMenuLabel>
              {PAUSE_OPTIONS.map((p) => (
                <DropdownMenuItem
                  key={p.minutes}
                  onClick={() => handlePause(p.minutes)}
                  disabled={setAiPause.isPending}
                  className="text-sm"
                >
                  <PauseCircle className="mr-2 h-4 w-4" />
                  {t(p.labelKey)}
                </DropdownMenuItem>
              ))}
              {isPaused && (
                <DropdownMenuItem
                  onClick={() => handlePause(0)}
                  disabled={setAiPause.isPending}
                  className="text-sm"
                >
                  <X className="mr-2 h-4 w-4" />
                  {t('chatPanel.unpause')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {onOpenInfo && (
            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t('chatPanel.customerInfo')} onClick={onOpenInfo}>
              <Info className="h-4 w-4" />
            </Button>
          )}

          {/* Menu thao tác */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative h-8 w-8" aria-label={t('chatPanel.conversationActions')}>
                <MoreVertical className="h-4 w-4" />
                {mobile && senderFilter !== 'all' && <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-primary" aria-hidden />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={cn('w-56', mobile && 'max-h-[var(--radix-dropdown-menu-content-available-height)] w-64 overflow-y-auto')}>
              {/* Điện thoại: người phụ trách (Của tôi / Giao lại) và bộ lọc tin nhắn gom vào đây để đầu khung chat gọn như ô chat Lark / DingTalk. */}
              {mobile && assign.state && (
                <>
                  <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{assignLabel(assign.state)}</DropdownMenuLabel>
                  {assign.state.kind === 'unassigned' && assign.state.canClaim && (
                    <DropdownMenuItem onClick={assign.onClaim} disabled={assign.claiming}>
                      <Hand className="mr-2 h-4 w-4" />
                      {t('chatPanel.claim')}
                    </DropdownMenuItem>
                  )}
                  {assign.state.canAssign && (
                    <DropdownMenuItem onClick={assign.openAssign}>
                      <UserRoundCog className="mr-2 h-4 w-4" />
                      {assign.state.kind === 'unassigned' ? t('chatPanel.assignToStaff') : t('chatPanel.reassign')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                </>
              )}
              {mobile && (
                <>
                  <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{t('chatPanel.filterMessages')}</DropdownMenuLabel>
                  <DropdownMenuRadioGroup value={senderFilter} onValueChange={(v) => { setSenderFilter(v as SenderFilter); setStaffId('') }}>
                    {SENDER_FILTERS.map((f) => (
                      <DropdownMenuRadioItem key={f.value} value={f.value} className="text-sm">{t(f.labelKey)}</DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                  {senderFilter === 'staff' && (senders.data?.staff.length ?? 0) > 1 && (
                    <>
                      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{t('chatPanel.staff')}</DropdownMenuLabel>
                      <DropdownMenuRadioGroup value={staffId || ALL_STAFF} onValueChange={(v) => setStaffId(v === ALL_STAFF ? '' : v)}>
                        <DropdownMenuRadioItem value={ALL_STAFF} className="text-sm">{t('chatPanel.allStaff')}</DropdownMenuRadioItem>
                        {senders.data!.staff.map((st) => (
                          <DropdownMenuRadioItem key={st.id} value={st.id} className="text-sm">{st.fullName} ({st.messages})</DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </>
                  )}
                  <DropdownMenuSeparator />
                </>
              )}
              {compact && (
                <DropdownMenuItem onClick={() => setResourcesOpen(true)} disabled={!convId}>
                  <Images className="mr-2 h-4 w-4" />
                  {t('chatPanel.libraryTitle')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleTogglePin} disabled={togglePin.isPending}>
                {conv?.isPinned ? (
                  <>
                    <PinOff className="mr-2 h-4 w-4" />
                    {t('common:actions.unpin')}
                  </>
                ) : (
                  <>
                    <Pin className="mr-2 h-4 w-4" />
                    {t('chatPanel.pinToTop')}
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleMarkUnread} disabled={markUnread.isPending}>
                <MailOpen className="mr-2 h-4 w-4" />
                {t('chatPanel.markUnread')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleMoveTab} disabled={!conv || setConvTab.isPending}>
                {conv?.tab === 'internal' ? (
                  <>
                    <Star className="mr-2 h-4 w-4" />
                    {t('chatPanel.moveToMain')}
                  </>
                ) : (
                  <>
                    <Users className="mr-2 h-4 w-4" />
                    {t('chatPanel.moveToInternal')}
                  </>
                )}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Khung gọn / điện thoại: người phụ trách + Nhận / Giao thành hàng riêng (đầu khung đã chật). */}
      {compact && !mobile && <AssignBar convId={convId} conv={conv} row />}
      {mobile && assign.dialog}

      {/* Điện thoại: bộ lọc nằm trong menu ⋮ (đỡ chiếm chiều cao); chỉ khi đang lọc mới hiện một dải mỏng để người dùng biết và bỏ lọc. */}
      {mobile && senderFilter !== 'all' && (
        <div className="flex items-center justify-between gap-2 border-b bg-primary/5 px-3 py-1.5 text-xs">
          <span className="min-w-0 truncate text-muted-foreground">
            {t('chatPanel.filtering')} <b className="font-semibold text-foreground">{t(activeFilter.labelKey)}</b>
            {senderFilter === 'staff' && staffId && senders.data?.staff.find((x) => x.id === staffId) ? ` · ${senders.data.staff.find((x) => x.id === staffId)!.fullName}` : ''}
          </span>
          <button type="button" className="shrink-0 font-medium text-primary" onClick={() => { setSenderFilter('all'); setStaffId('') }}>{t('chatPanel.clearFilter')}</button>
        </div>
      )}

      {/* Bộ lọc tin nhắn theo người gửi (máy tính) */}
      {!mobile && (
      <div className="flex items-center gap-1.5 overflow-x-auto border-b bg-background/40 px-3 py-2">
        {SENDER_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => { setSenderFilter(f.value); setStaffId('') }}
            className={cn(
              'shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              senderFilter === f.value
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-card text-muted-foreground hover:bg-muted',
            )}
          >
            {t(f.labelKey)}
          </button>
        ))}
        {/* Nhân viên ▸ từng người (chỉ khi hội thoại có từ 2 nhân viên gửi tin) */}
        {senderFilter === 'staff' && (senders.data?.staff.length ?? 0) > 1 && (
          <Select value={staffId || ALL_STAFF} onValueChange={(v) => setStaffId(v === ALL_STAFF ? '' : v)}>
            <SelectTrigger className="h-7 w-auto shrink-0 gap-1 rounded-full px-3 text-xs" aria-label={t('chatPanel.chooseStaff')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_STAFF}>{t('chatPanel.allStaff')}</SelectItem>
              {senders.data!.staff.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.fullName} ({s.messages})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      )}

      {/* Danh sách tin nhắn */}
      {/* overflow-x-hidden + min-w-0: overflow-y-auto khiến trục ngang cũng thành `auto` — chỉ một phần tử rộng hơn khung (tệp tên dài, link, thẻ…) là cả
          danh sách trượt ngang, tin bị cắt mất nửa trái (lỗi trên iPhone). */}
      <div
        ref={scrollRef}
        onScroll={(e) => { if (e.currentTarget.scrollTop < 160) loadOlder() }}
        className="min-w-0 flex-1 space-y-1 overflow-y-auto overflow-x-hidden bg-background/40 p-4 max-md:px-3"
      >
        {listLoading ? (
          <Loading label={t('chatPanel.loadingMessages')} />
        ) : listError ? (
          <ErrorState message={t('chatPanel.loadError')} />
        ) : messages.length === 0 ? (
          msgSearch.trim()
            ? <EmptyState title={t('chatPanel.noMatch')} description={t('chatPanel.tryOtherKeyword')} />
            : senderFilter !== 'all'
              ? <EmptyState title={t('chatPanel.noFilteredMessages', { filter: t(activeFilter.labelKey) })} description={t(activeFilter.emptyKey)} />
              : <EmptyState title={t('chatPanel.noMessages')} description={t('chatPanel.sendFirst')} />
        ) : (
          <>
          {canLoadOlder && isFetchingNextPage && (
            <div className="flex justify-center py-2" role="status" aria-label={t('chatPanel.loadingMessages')}>
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              actions={messageActions}
              seenViewers={seenMarkers[m.id]}
              senderAvatarUrl={isGroup && m.senderUid ? groupMemberAvatarByUid[m.senderUid] : undefined}
              accountId={isGroup && isZaloUser ? conv?.channelAccount?.id : undefined}
              convId={convId}
            />
          ))}
          </>
        )}

        {(typingName || aiTyping) && (
          <div className="flex items-center gap-2 px-1 py-1 text-xs text-muted-foreground">
            <span className="flex gap-0.5">
              <Dot /> <Dot /> <Dot />
            </span>
            {aiTyping ? t('chatPanel.aiTyping') : t('chatPanel.typing', { name: typingName })}
          </div>
        )}
      </div>

      {/* Bản nháp AI */}
      {aiDraft && (
        <div className="flex items-start gap-2 border-t bg-primary/5 px-4 py-2">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-2 text-xs font-medium text-primary">
              {t('chatPanel.aiSuggestion')}
              <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                {Math.round((aiDraft.confidence || 0) * 100)}%
              </Badge>
            </p>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{aiDraft.content}</p>
          </div>
          <Button size="sm" variant="outline" className="h-7 shrink-0 text-xs" onClick={applyDraft}>
            {t('chatPanel.use')}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 shrink-0"
            onClick={clearAiDraft}
            aria-label={t('chatPanel.dismissSuggestion')}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Bảng gợi ý AI */}
      {suggestions && (
        <div className="border-t border-primary/30 bg-primary/5">
          <div className="flex items-center justify-between px-3 py-2">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-primary">
              <Sparkles className="h-4 w-4" />
              {t('chatPanel.aiSuggestion')}
            </span>
            <button
              type="button"
              onClick={() => setSuggestions(null)}
              aria-label={t('chatPanel.closeSuggestions')}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <ul className="max-h-48 divide-y divide-primary/10 overflow-y-auto border-t border-primary/10">
            {suggestions.map((s) => {
              const meta = TONE_META[s.tone]
                ? { label: t(TONE_META[s.tone].labelKey), cls: TONE_META[s.tone].cls }
                : { label: s.tone, cls: 'bg-muted text-muted-foreground' }
              return (
                <li key={s.suggestionId}>
                  <button
                    type="button"
                    onClick={() => {
                      setText(s.text)
                      setSuggestions(null)
                    }}
                    className="w-full px-3 py-2 text-left transition-colors hover:bg-primary/10"
                    title={t('chatPanel.clickToUse')}
                  >
                    <span
                      className={cn(
                        'mb-1 inline-block rounded-md px-2 py-0.5 text-xs font-semibold',
                        meta.cls,
                      )}
                    >
                      {meta.label}
                    </span>
                    <p className="line-clamp-2 text-sm text-foreground">{s.text}</p>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {/* Tài khoản gửi đang rớt / đang kết nối lại: nói rõ TRƯỚC khi nhân viên gõ, vì
          tin gửi lúc này có thể chỉ nằm trong CRM (Message.sendError), khách không nhận được. */}
      {isAccountDown(sendingAccount) && <DisconnectedBanner account={sendingAccount!} />}

      {/* Đang trả lời tin nhắn nào */}
      {replyTo && (
        <div className="flex items-center gap-2 border-t bg-muted/50 px-3 py-2">
          <Reply className="h-4 w-4 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-primary">
              {t('chatPanel.replyingTo', { name: replyTo.senderType === 'self' ? t('chatPanel.yourself') : replyTo.senderName || t('chatPanel.customerLower') })}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {messagePreview({
                content: replyTo.content,
                contentType: replyTo.contentType,
                senderType: replyTo.senderType,
                isDeleted: replyTo.isDeleted,
              } as never)}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setReplyTo(null)}
            aria-label={t('chatPanel.cancelReply')}
            className="shrink-0 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Khung soạn tin kiểu SaleSmartly: tab chế độ gửi → ô nhập không viền →
          thanh công cụ + nút gửi, gói trong một khung bo góc. Màu khung đổi theo
          chế độ (vàng nội bộ, tím riêng tư) để không gửi nhầm cho khách. */}
      {/* data-keyboard (use-keyboard-viewport.ts): bàn phím mở thì không còn thanh Home → bỏ vùng đệm an toàn dưới. */}
      <div className={cn('px-3 pb-3 pt-1', mobile && 'pb-[calc(0.5rem+env(safe-area-inset-bottom))] [html[data-keyboard]_&]:pb-2')}>
        <div
          className={cn(
            'space-y-2 rounded-xl border bg-card pb-2 transition-colors focus-within:border-primary/50',
            sendMode === 'internal' && 'border-amber-300 bg-amber-50/50 focus-within:border-amber-400 dark:border-amber-700 dark:bg-amber-950/20',
            sendMode === 'private' && 'border-violet-300 bg-violet-50/50 focus-within:border-violet-400 dark:border-violet-700 dark:bg-violet-950/20',
          )}
        >
          {/* Cột chat hẹp lại (màn hình nhỏ / cột thông tin khách kéo rộng) mà hàng tab không co được (mỗi tab
              `shrink-0`+`whitespace-nowrap`) → cho cuộn ngang thay vì tràn ra ngoài khung soạn (từng đè lên cột
              thông tin khách bên cạnh vì khung soạn không có gì chặn tràn). */}
          <div role="tablist" aria-label={t('chatPanel.sendMode')} className={cn('flex items-center overflow-x-auto border-b px-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden', mobile ? 'gap-4' : 'gap-5')}>
            {SEND_MODES.map((m) => {
              const active = sendMode === m.value
              const Icon = m.icon
              return (
                <button
                  key={m.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  title={t(m.hintKey)}
                  onClick={() => switchSendMode(m.value)}
                  className={cn(
                    '-mb-px flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 py-2 text-sm transition-colors',
                    active ? m.activeClass : 'border-transparent text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Icon className="h-3.5 w-3.5" /> {mobile ? t(m.shortLabelKey) : t(m.labelKey)}
                </button>
              )
            })}
            {/* Chỉ owner/admin thấy — tự ẩn với vai trò khác; điện thoại không có (chức năng quản trị). */}
            {!mobile && (
              <div className="ml-auto shrink-0">
                <ViewAsStaffButton />
              </div>
            )}
          </div>

          {/* Dòng nhắc chống gửi nhầm + mention nội bộ — chỉ là chữ; khung soạn đã được tô viền màu. */}
          {sendMode !== 'external' && (
            <div
              className={cn(
                'flex flex-wrap items-center gap-x-2 gap-y-1 px-3 pt-2 text-xs',
                sendMode === 'internal' ? 'text-amber-800 dark:text-amber-300' : 'text-violet-800 dark:text-violet-300',
              )}
            >
              <span className="font-semibold">
                {sendMode === 'internal' ? t('chatPanel.sendingInternal') : t('chatPanel.sendingPrivate')}
              </span>
              <span className="opacity-80">
                {sendMode === 'internal'
                  ? t('chatPanel.internalVisibility')
                  : t('chatPanel.privateVisibility')}
              </span>
              <span className="mx-1 opacity-40">|</span>
              {internalMentions.length === 0 ? (
                <span className="opacity-70">{t('chatPanel.typeAtToTag')}</span>
              ) : (
                internalMentions.map((m) => (
                  <span key={m.id} className="inline-flex items-center gap-1 rounded-full border border-current/30 bg-white/60 px-2 py-0.5 dark:bg-black/20">
                    @{m.fullName}
                    <button type="button" onClick={() => setInternalMentions((cur) => cur.filter((x) => x.id !== m.id))}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))
              )}
            </div>
          )}

          {mentionQuery && mentionCandidates.length > 0 && (
            <div className="relative">
              <ul className="absolute bottom-1 left-0 z-30 max-h-56 w-72 overflow-y-auto rounded-xl border bg-popover p-1 shadow-lg">
                {mentionCandidates.map((m, i) => (
                  <li key={m.uid}>
                    <button
                      type="button"
                      onMouseEnter={() => setMentionIndex(i)}
                      onClick={() => pickMention(m)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm',
                        i === mentionIndex ? 'bg-accent' : 'hover:bg-accent/60',
                      )}
                    >
                      <Avatar className="h-6 w-6 shrink-0">
                        {m.avatarUrl && <AvatarImage src={m.avatarUrl} alt="" />}
                        <AvatarFallback className="text-[10px]">{initials(m.name)}</AvatarFallback>
                      </Avatar>
                      <span className="min-w-0 flex-1 truncate">{m.name}</span>
                      {m.uid === MENTION_ALL_UID && (
                        <span className="shrink-0 text-[10px] text-muted-foreground">{t('chatPanel.wholeGroup')}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Ảnh dán đang chờ — bấm Gửi mới đi, × để xoá ảnh dán nhầm */}
          {pendingImages.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 p-2">
              {pendingImages.map((p, i) => (
                <div key={p.url} className="relative">
                  <img src={p.url} alt="" className="h-16 w-16 rounded-md border object-cover" />
                  <button
                    type="button"
                    aria-label={t('chatPanel.removeImage')}
                    onClick={() => {
                      URL.revokeObjectURL(p.url)
                      setPendingImages((prev) => prev.filter((_, idx) => idx !== i))
                    }}
                    className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-foreground/80 text-background shadow hover:bg-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <span className="text-[11px] text-muted-foreground">
                {text.trim() ? t('chatPanel.pendingImagesWithText', { count: pendingImages.length }) : t('chatPanel.pendingImages', { count: pendingImages.length })}
              </span>
            </div>
          )}

          <div className="relative">
            {/* AI gợi ý nằm ngay góc ô soạn: gợi ý điền vào chính ô này nên đặt cạnh chữ, không lẫn với
                các nút gửi nội dung ở thanh công cụ. Ô soạn chừa lề phải (pr-28) để chip không đè chữ. */}
            <button
              type="button"
              onClick={handleSuggest}
              disabled={suggesting}
              aria-label={t('chatPanel.aiSuggestion')}
              title={t('chatPanel.aiSuggestHint')}
              className="absolute right-2 top-0.5 z-10 inline-flex h-6 items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-2 text-[11px] font-medium text-primary transition-colors hover:bg-primary/10 disabled:opacity-60"
            >
              {suggesting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              {t('chatPanel.aiSuggestShort')}
            </button>
            <Textarea
              ref={composerRef}
              onPaste={(e) => {
                // Dán ảnh trực tiếp vào ô chat (chụp màn hình / copy ảnh) → gửi
                // như bấm nút "Gửi ảnh". Dán chữ vẫn hoạt động bình thường.
                const imgs = Array.from(e.clipboardData?.items ?? [])
                  .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
                  .map((it) => it.getAsFile())
                  .filter((f): f is File => !!f)
                if (imgs.length === 0) return
                e.preventDefault()
                if (sendMode !== 'external') {
                  // Đường gửi ảnh hiện tại đi THẲNG ra kênh khách — chặn để không
                  // lộ ảnh khi đang ở chế độ nội bộ/riêng tư.
                  toast.warning(t('chatPanel.noImagesInInternal'))
                  return
                }
                setPendingImages((prev) => {
                  const next = [...prev, ...imgs.map((f) => ({ file: f, url: URL.createObjectURL(f) }))]
                  if (next.length > 10) {
                    toast.warning(t('chatPanel.maxImages'))
                    next.slice(10).forEach((p) => URL.revokeObjectURL(p.url))
                    return next.slice(0, 10)
                  }
                  return next
                })
              }}
              value={text}
              onChange={(e) => {
                setText(e.target.value)
                emitTyping()
                // Gửi khách: chỉ nhóm mới tag được (Zalo bỏ qua mention ở chat 1-1).
                // Nội bộ/riêng tư: luôn tag được — gợi ý danh bạ nhân viên.
                const canMention = sendMode !== 'external' || isGroup
                setMentionQuery(canMention ? detectMentionQuery(e.target.value, e.target.selectionStart ?? 0) : null)
                setMentionIndex(0)
              }}
              onKeyDown={(e) => {
                // Đang mở danh sách '@' thì phím mũi tên/Enter thuộc về danh sách,
                // không được để lọt xuống thành gửi tin.
                if (mentionQuery && mentionCandidates.length > 0) {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault()
                    setMentionIndex((i) => (i + 1) % mentionCandidates.length)
                    return
                  }
                  if (e.key === 'ArrowUp') {
                    e.preventDefault()
                    setMentionIndex((i) => (i - 1 + mentionCandidates.length) % mentionCandidates.length)
                    return
                  }
                  if ((e.key === 'Enter' || e.key === 'Tab') && !e.nativeEvent.isComposing) {
                    e.preventDefault()
                    pickMention(mentionCandidates[mentionIndex])
                    return
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault()
                    setMentionQuery(null)
                    return
                  }
                }
                // isComposing: bộ gõ tiếng Việt (Telex/VNI) chưa "chốt" ký tự cuối.
                // Nếu gửi lúc này, ký tự đó sẽ được commit lại vào ô trống sau khi xoá.
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault()
                  handleSend()
                }
              }}
              placeholder={mobile ? t('chatPanel.placeholderMobile') : t('chatPanel.placeholderDesktop')}
              className={cn('min-h-[60px] w-full resize-none border-0 bg-transparent py-1 pl-3 pr-28 shadow-none focus-visible:ring-0', mobile && 'min-h-[44px] pr-24 text-base')}
              rows={1}
            />
          </div>

          {/* Ô chọn tệp ẩn — dùng chung máy tính + điện thoại */}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFilePicked}
            aria-hidden
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFilePicked}
            aria-hidden
          />

          {/* Thanh công cụ + nút gửi */}
          {mobile ? (
            // Điện thoại: gọn kiểu ô chat Claude mobile — "+" (hộp Tiện ích) + vài tiện ích GHIM (người dùng tự chọn / xếp) + nút gửi tròn.
            <MobileComposerToolbar
              pinned={composerTools.tools}
              onPinnedChange={composerTools.setTools}
              ctx={toolCtx}
              sendButton={sendButton}
            />
          ) : (
          <div className="flex flex-wrap items-center gap-1 px-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground"
              onClick={() => imageInputRef.current?.click()}
              disabled={uploading}
              aria-label={t('chatPanel.sendImageFromDevice')}
              title={t('chatPanel.sendImage')}
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              aria-label={t('chatPanel.sendFileFromDevice')}
              title={t('chatPanel.sendFile')}
            >
              <Paperclip className="h-4 w-4" />
            </Button>

            <StickerPicker convId={convId} />

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground"
              onClick={() => setLibraryOpen(true)}
              aria-label={t('chatPanel.systemLibrary')}
              title={t('chatPanel.systemLibrary')}
            >
              <FolderOpen className="h-4 w-4" />
            </Button>

            <QuickMessagesPopover
              accountId={conv?.channelAccount?.id}
              contact={conv?.contact}
              onPick={(picked) => setText((prev) => (prev ? `${prev}\n${picked}` : picked))}
            />

            {/* Thao tác nâng cao — gom lại cho gọn giống Zalo */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground"
                  aria-label={t('chatPanel.moreOptions')}
                  title={t('chatPanel.moreOptions')}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="top" className="w-56">
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                  {t('chatPanel.sendContent')}
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setExtrasTab('card')}>
                  <IdCard className="mr-2 h-4 w-4" />
                  {t('chatPanel.contactCard')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setExtrasTab('bank')}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  {t('chatPanel.bankCard')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setExtrasTab('link')}>
                  <Link2 className="mr-2 h-4 w-4" />
                  {t('chatPanel.linkPreview')}
                </DropdownMenuItem>

                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                  {t('chatPanel.conversationTools')}
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setReminderOpen(true)}>
                  <BellRing className="mr-2 h-4 w-4" />
                  {t('chatPanel.reminder')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setPollOpen(true)}>
                  <BarChart3 className="mr-2 h-4 w-4" />
                  {t('chatPanel.pollGroup')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Gửi sản phẩm (Evotech / tạo trong eCDP) — chỉ ở chế độ Trả lời: tin này đi thẳng ra kênh khách. */}
            <ProductPickerButton
              convId={convId}
              disabled={sendMode !== 'external'}
              disabledHint={t('chatPanel.productReplyOnly')}
            />

            {/* Tạo đơn Evotech cho khách của hội thoại (chỉ hiện khi đã kết nối Evotech). */}
            <CreateOrderButton convId={convId} contactId={conv?.contact?.id} />

            {/* Nút gửi ĐỔI THEO CHẾ ĐỘ — không dùng một nút "Gửi" chung (chống gửi nhầm) */}
            {sendButton}
          </div>
          )}
        </div>
      </div>

      <ForwardDialog
        sourceConvId={convId}
        messageId={forwardMsg?.id ?? null}
        messageText={forwardMsg?.content ?? ''}
        messageContentType={forwardMsg?.contentType ?? ''}
        open={!!forwardMsg}
        onOpenChange={(v) => !v && setForwardMsg(null)}
      />

      <SendExtrasDialog
        convId={convId}
        accountId={conv?.channelAccount?.id}
        open={!!extrasTab}
        onOpenChange={(v) => !v && setExtrasTab(null)}
        defaultTab={extrasTab ?? 'card'}
      />

      <ReminderDialog convId={convId} open={reminderOpen} onOpenChange={setReminderOpen} />

      <PollDialog
        convId={convId}
        isGroup={conv?.threadType === 'group'}
        open={pollOpen}
        onOpenChange={setPollOpen}
      />

      <MediaLibraryDialog
        convId={convId}
        contactId={conv?.contact?.id}
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
      />

      <ResourcesDialog
        conversationId={convId}
        open={resourcesOpen}
        onOpenChange={setResourcesOpen}
      />
    </div>
  )
}

/** Tab chế độ gửi ở đầu khung soạn tin. */
const SEND_MODES = [
  {
    value: 'external' as const,
    labelKey: 'chatPanel.mode.replyLabel',
    shortLabelKey: 'chatPanel.mode.replyShort',
    hintKey: 'chatPanel.mode.replyHint',
    icon: Send,
    activeClass: 'border-primary font-medium text-primary',
  },
  {
    value: 'internal' as const,
    labelKey: 'chatPanel.mode.internalLabel',
    shortLabelKey: 'chatPanel.mode.internalShort',
    hintKey: 'chatPanel.mode.internalHint',
    icon: Lock,
    activeClass: 'border-amber-500 font-medium text-amber-700 dark:text-amber-300',
  },
  {
    value: 'private' as const,
    labelKey: 'chatPanel.mode.privateLabel',
    shortLabelKey: 'chatPanel.mode.privateShort',
    hintKey: 'chatPanel.mode.privateHint',
    icon: LockKeyhole,
    activeClass: 'border-violet-500 font-medium text-violet-700 dark:text-violet-300',
  },
]

function Dot() {
  return <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground" />
}
